import { useState } from "react";
import { Copy, Check, Key } from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { Modal } from "../../components/Modal/Modal";
import styles from "./RevealKeyModal.module.css";

interface Props {
  /** Plaintext API key to reveal; when null the modal is closed. */
  apiKey: string | null;
  onClose: () => void;
}

/**
 * Post-rotation confirmation. Neutral tone — the key is always retrievable
 * from the API Keys page, so the modal only provides immediate copy access
 * and reminds the admin that the previous key has been revoked.
 */
export function RevealKeyModal({ apiKey, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  if (!apiKey) return null;

  async function copy() {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (e.g. non-secure context) — silently ignore.
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="API key rotated"
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className={styles.headerIcon} aria-hidden="true">
        <Key size={18} />
      </div>

      <p className={styles.lead}>
        Your new API key is ready. Copy it now if you want, or grab it later
        from the API Keys page.
      </p>

      <div className={styles.keyBox}>
        <code className={styles.key}>{apiKey}</code>
        <button
          type="button"
          className={`${styles.copyBtn} ${copied ? styles.copyBtnCopied : ""}`}
          onClick={() => void copy()}
        >
          {copied ? (
            <Check size={12} aria-hidden="true" />
          ) : (
            <Copy size={12} aria-hidden="true" />
          )}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>

      <div className={styles.alertWrap}>
        <Alert variant="info">
          The previous key was revoked. Put the new value into your game
          server&apos;s secret store — it is also readable here at any time.
        </Alert>
      </div>
    </Modal>
  );
}
